
import { useScrollAnimation, useStaggerAnimation } from '../../../hooks/useScrollAnimation';

const countries = [
  {
    flag: '🇩🇪',
    name: 'Alemania',
    capital: 'Berlín',
    icon: 'ri-building-2-line',
    highlight: 'Mayor economía de Europa',
    description:
      'Mercado laboral altamente estructurado con fuerte demanda en ingeniería, tecnología, sanidad y logística. Cultura profesional basada en la precisión, la puntualidad y el rigor técnico.',
    sectors: ['Ingeniería', 'Tecnología', 'Sanidad', 'Logística'],
    color: '#1A2B4A',
  },
  {
    flag: '🇨🇭',
    name: 'Suiza',
    capital: 'Zúrich / Ginebra',
    icon: 'ri-bank-line',
    highlight: 'Salarios más altos de Europa',
    description:
      'País plurilingüe con uno de los mercados laborales más competitivos y mejor remunerados del mundo. Alta demanda en finanzas, farmacéutica, hostelería de lujo y servicios internacionales.',
    sectors: ['Finanzas', 'Farmacéutica', 'Hostelería', 'Servicios'],
    color: '#8B1A1A',
  },
  {
    flag: '🇳🇱',
    name: 'Países Bajos',
    capital: 'Ámsterdam',
    icon: 'ri-global-line',
    highlight: 'Hub logístico europeo',
    description:
      'Economía abierta e internacional con sede de grandes multinacionales. Entorno laboral flexible, alto nivel de inglés y fuerte demanda en tecnología, comercio internacional y logística.',
    sectors: ['Tecnología', 'Comercio', 'Logística', 'Multinacionales'],
    color: '#1A4A2B',
  },
  {
    flag: '🇦🇹',
    name: 'Austria',
    capital: 'Viena',
    icon: 'ri-hotel-line',
    highlight: 'Puerta a Europa Central',
    description:
      'Economía estable con fuerte presencia en turismo, industria manufacturera y servicios financieros. Viena es sede de organismos internacionales y punto estratégico entre Europa Occidental y Oriental.',
    sectors: ['Turismo', 'Manufactura', 'Finanzas', 'Organismos Int.'],
    color: '#4A1A2B',
  },
  {
    flag: '🇧🇪',
    name: 'Bélgica',
    capital: 'Bruselas',
    icon: 'ri-government-line',
    highlight: 'Capital de la Unión Europea',
    description:
      'Sede de las instituciones europeas y de numerosas organizaciones internacionales. Mercado trilingüe con alta demanda en asuntos europeos, farmacéutica, tecnología y servicios corporativos.',
    sectors: ['Asuntos EU', 'Farmacéutica', 'Tecnología', 'Corporativo'],
    color: '#2B3A1A',
  },
];

export default function InternationalReach() {
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: cardsRef, visibleItems } = useStaggerAnimation(5, { threshold: 0.06 });

  return (
    <section className="py-32 bg-[#F8F6F3]">
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className="text-center mb-20"
          style={{
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(36px)',
            transition: 'opacity 0.8s ease, transform 0.8s ease',
          }}
        >
          <span className="inline-block text-xs font-semibold tracking-[0.25em] uppercase text-[#C9A84C] mb-4">
            Mercados prioritarios
          </span>
          <h2 className="text-5xl font-bold text-[#1A2B4A] mb-6">Alcance europeo</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Operamos en los mercados europeos con mayor demanda de talento internacional. Conocemos en profundidad
            la cultura laboral, el marco legal y las oportunidades de cada país.
          </p>
        </div>

        {/* Country Cards */}
        <div
          ref={cardsRef as React.RefObject<HTMLDivElement>}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {countries.map((country, i) => (
            <div
              key={country.name}
              className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300 group"
              style={{
                opacity: visibleItems[i] ? 1 : 0,
                transform: visibleItems[i] ? 'translateY(0)' : 'translateY(40px)',
                transition: 'opacity 0.6s ease, transform 0.6s ease',
              }}
            >
              {/* Card Header */}
              <div
                className="px-7 py-6 flex items-center gap-4"
                style={{ backgroundColor: country.color }}
              >
                <span className="text-4xl">{country.flag}</span>
                <div>
                  <h3 className="text-xl font-bold text-white">{country.name}</h3>
                  <p className="text-white/60 text-sm">{country.capital}</p>
                </div>
                <div className="ml-auto w-10 h-10 flex items-center justify-center rounded-full bg-white/10">
                  <i className={`${country.icon} text-lg text-white`}></i>
                </div>
              </div>

              {/* Highlight Badge */}
              <div className="px-7 pt-5">
                <span className="inline-block text-xs font-semibold tracking-widest uppercase text-[#C9A84C] border border-[#C9A84C]/30 rounded-full px-3 py-1">
                  {country.highlight}
                </span>
              </div>

              {/* Description */}
              <div className="px-7 py-4">
                <p className="text-gray-600 text-sm leading-relaxed">{country.description}</p>
              </div>

              {/* Sectors */}
              <div className="px-7 pb-7">
                <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Sectores clave</p>
                <div className="flex flex-wrap gap-2">
                  {country.sectors.map((sector) => (
                    <span
                      key={sector}
                      className="text-xs font-medium px-3 py-1 rounded-full bg-gray-100 text-gray-700"
                    >
                      {sector}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* Closing card — "More countries" */}
          <div
            className="bg-[#1A2B4A] rounded-2xl overflow-hidden shadow-sm border border-[#1A2B4A] flex flex-col items-center justify-center px-8 py-12 text-center"
            style={{
              opacity: visibleItems[4] ? 1 : 0,
              transform: visibleItems[4] ? 'translateY(0)' : 'translateY(40px)',
              transition: 'opacity 0.6s ease 0.25s, transform 0.6s ease 0.25s',
            }}
          >
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/10 mb-5">
              <i className="ri-earth-line text-2xl text-[#C9A84C]"></i>
            </div>
            <h3 className="text-xl font-bold text-white mb-3">¿Otro país europeo?</h3>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              También gestionamos procesos en otros mercados europeos según las necesidades específicas de cada cliente.
            </p>
            <a
              href="#contact"
              className="inline-block text-sm font-semibold text-[#C9A84C] border border-[#C9A84C]/40 rounded-full px-6 py-2 hover:bg-[#C9A84C]/10 transition-colors whitespace-nowrap"
            >
              Consultar disponibilidad
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
